import { useState } from 'react';
import { Button, Card } from '@hsc/ui';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { useMe } from '../../shared/api/useMe.js';
import { changePassword } from '../../shared/api/auth.js';
import { resolveDashboardKind } from '../../shared/nav/resolve-role.js';
import { BackButton } from '../../shared/ui/BackButton.js';
import { initials, roleLabel } from '../shell/shell-util.js';
import { useSetShellHeader } from '../shell/shell-header.js';
import {
  isCategoryOn,
  useNotificationPreferences,
  useTelegramBindLink,
  useToggleGuestCategory,
  type GuestNotifCategory,
} from './useProfilePrefs.js';

/**
 * Экран «Профиль».
 *
 * ГОСТЬ (роль guest) — упрощённый профиль (8 правок Артёма):
 *   1. Фамилия + отдельное поле «Номер комнаты» (из активного гранта).
 *   2. Нет email.
 *   3. Нет кнопки «Изменить» (данные из проживания, гость их не правит).
 *   4. Нет блока «Безопасность» (2FA/пароль/сессии).
 *   5. Telegram — «Привязать» (deep-link бота) если не привязан, иначе «Отвязать».
 *   6. Уведомления — ползунки работают НАПРЯМУЮ (без кнопки «Изменить»).
 *   7. Нет SLA-норматива.
 *   8. Гостевые категории: «статус заявки» + «сообщение в чате».
 *
 * ПЕРСОНАЛ — прежний экран manager v3 §2.2 (read-only + заглушки).
 */

/** Read-only строка «Лейбл: значение» (+опц. chip «нельзя изменить»). */
function Field({
  label,
  value,
  locked,
}: {
  label: string;
  value: string;
  locked?: boolean;
}): JSX.Element {
  return (
    <div className="pf-field">
      <span className="pf-field__label">{label}:</span>{' '}
      <strong className="pf-field__value">{value}</strong>
      {locked ? <span className="pf-chip pf-chip--lock">нельзя изменить</span> : null}
    </div>
  );
}

/** Read-only toggle (визуальный статус канала, без мутации). */
function ChannelRow({ children, on }: { children: React.ReactNode; on: boolean }): JSX.Element {
  return (
    <div className="pf-channel">
      <span className="pf-channel__text">{children}</span>
      <span
        className={`pf-toggle${on ? ' on' : ''}`}
        role="img"
        aria-label={on ? 'включено' : 'выключено'}
      />
    </div>
  );
}

/** Рабочий ползунок гостевой категории — переключает СРАЗУ (PUT). */
function GuestToggleRow({
  label,
  category,
  on,
  disabled,
  onToggle,
  testid,
}: {
  label: React.ReactNode;
  category: GuestNotifCategory;
  on: boolean;
  disabled: boolean;
  onToggle: (category: GuestNotifCategory, next: boolean) => void;
  testid: string;
}): JSX.Element {
  return (
    <div className="pf-channel">
      <span className="pf-channel__text">{label}</span>
      <button
        type="button"
        className={`pf-toggle pf-toggle--btn${on ? ' on' : ''}`}
        role="switch"
        aria-checked={on}
        aria-label={on ? 'включено' : 'выключено'}
        data-testid={testid}
        disabled={disabled}
        onClick={() => onToggle(category, !on)}
      />
    </div>
  );
}

/**
 * Отдельный экран «Сменить пароль» персонала/менеджера (#80) — canon формы:
 * «Назад» сверху, поля текущий/новый/повтор, кнопки Сохранить+Назад снизу.
 * Бэк: POST /v1/auth/change-password (текущий пароль + новый, политика ≥12).
 */
function ChangePasswordScreen({
  userId,
  onBack,
}: {
  userId: string | undefined;
  onBack: () => void;
}): JSX.Element {
  const [oldPassword, setOld] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  const canSubmit =
    !pending &&
    oldPassword.length > 0 &&
    newPassword.length >= 12 &&
    newPassword === confirm;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!userId) {
      setError('Не удалось определить пользователя. Обновите страницу.');
      return;
    }
    if (newPassword.length < 12) {
      setError('Новый пароль — минимум 12 символов.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Новый пароль и подтверждение не совпадают.');
      return;
    }
    setPending(true);
    try {
      await changePassword({
        user_id: userId,
        old_password: oldPassword,
        new_password: newPassword,
      });
      setDone(true);
      setOld('');
      setNew('');
      setConfirm('');
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 401) setError('Текущий пароль неверный.');
      else if (status === 422) setError('Новый пароль слишком простой (минимум 12 символов).');
      else setError('Не удалось сменить пароль. Попробуйте позже.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section data-testid="module-profile" className="pf">
      <BackButton onClick={onBack} testId="change-password-back" />
      <Card>
        <h2 className="hsc-card__title">Смена пароля</h2>
        {done ? (
          <div className="pf-banner" role="status" data-testid="change-password-success">
            Пароль изменён.
          </div>
        ) : null}
        {error ? (
          <div className="pf-banner pf-banner--error" role="alert" data-testid="change-password-error">
            {error}
          </div>
        ) : null}
        <form className="hsc-stack" onSubmit={(e) => void onSubmit(e)}>
          <label className="hsc-field">
            <span className="hsc-field__label">Текущий пароль</span>
            <input
              className="hsc-input"
              type="password"
              autoComplete="current-password"
              value={oldPassword}
              onChange={(e) => setOld(e.target.value)}
              data-testid="change-password-old"
            />
          </label>
          <label className="hsc-field">
            <span className="hsc-field__label">Новый пароль (минимум 12 символов)</span>
            <input
              className="hsc-input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              data-testid="change-password-new"
            />
          </label>
          <label className="hsc-field">
            <span className="hsc-field__label">Повторите новый пароль</span>
            <input
              className="hsc-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-password-confirm"
            />
          </label>
          <div className="folio-form__row">
            <Button type="submit" disabled={!canSubmit} data-testid="change-password-submit">
              {pending ? 'Сохраняем…' : 'Сохранить'}
            </Button>
            <Button type="button" variant="secondary" onClick={onBack}>
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export function ProfilePage(): JSX.Element {
  const platform = usePlatformMe();
  const me = useMe();
  // Смена пароля — отдельный экран (#80, canon формы).
  const [securityView, setSecurityView] = useState<'main' | 'change-password'>('main');
  const [supportHint, setSupportHint] = useState<string | null>(null);

  const name = platform.data?.actor.name ?? me.data?.display_name ?? '—';

  const actorRoles = platform.data?.actor.roles ?? me.data?.roles ?? [];
  const isGuest = actorRoles.includes('guest');

  useSetShellHeader({
    title: isGuest ? (me.data?.surname || name) : name,
    hint: isGuest ? (
      <span className="pf-tophint">Данные из вашего проживания</span>
    ) : (
      <span className="pf-tophint">Изменения применяются по каждой карточке</span>
    ),
  });

  if (platform.isLoading) {
    return (
      <section data-testid="module-profile">
        <p className="ta-muted">Загрузка профиля…</p>
      </section>
    );
  }

  if (isGuest) {
    return <GuestProfile name={name} />;
  }

  // Смена пароля — отдельный экран (только персонал/менеджер, не гость).
  if (securityView === 'change-password') {
    return (
      <ChangePasswordScreen
        userId={me.data?.id ?? platform.data?.actor.id}
        onBack={() => setSecurityView('main')}
      />
    );
  }

  if (platform.isError || !platform.data) {
    return (
      <section data-testid="module-profile">
        <Card>
          <h2 className="hsc-card__title">Не удалось загрузить профиль</h2>
          <p>Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }

  const { actor } = platform.data;
  const kind = resolveDashboardKind(actor.roles);
  const primaryRole = actor.roles[0] ?? kind;
  const roleText = roleLabel(primaryRole);
  const email = me.data?.email ?? '—';
  const [first = name, ...rest] = name.split(/\s+/).filter(Boolean);
  const last = rest.join(' ');

  /** Заглушка-действие без мутации: информирует, что функция готовится. */
  const stub = (msg: string) => () => setSupportHint(msg);

  return (
    <section data-testid="module-profile" className="pf">
      {supportHint ? (
        <div className="pf-banner" role="status" data-testid="profile-hint">
          {supportHint}
        </div>
      ) : null}

      <div className="pf-grid">
        {/* 1. Личные данные */}
        <Card>
          <div className="pf-card-head">
            <h4 className="pf-card-title">Личные данные</h4>
            <button
              type="button"
              className="pf-btn pf-btn--ghost"
              data-testid="profile-edit-personal"
              onClick={stub('Редактирование профиля скоро будет доступно. Email и телефон меняются через поддержку.')}
            >
              Изменить
            </button>
          </div>

          <div className="pf-identity">
            <span className="ta-avatar ta-avatar--lg" aria-hidden="true">
              {initials(name)}
            </span>
            <div>
              <div className="pf-identity__name">{name}</div>
              <div className="pf-identity__badge">{roleText}</div>
            </div>
          </div>

          <div className="pf-fields">
            <Field label="Имя" value={first} />
            {last ? <Field label="Фамилия" value={last} /> : null}
            <Field label="Должность" value={roleText} />
            <Field label="Email" value={email} locked />
            <Field label="Телефон" value="—" locked />
            <Field label="Язык" value="Русский" />
            <Field label="Тимзона" value="Europe/Moscow (UTC+3)" />
          </div>

          <p className="pf-footnote">
            Email + телефон — для смены пишите в поддержку (нужна верификация).
          </p>
        </Card>

        <div className="pf-stack">
          {/* 2. Безопасность — collapsible */}
          <Card>
            <h4 className="pf-card-title">Безопасность</h4>
            <div className="pf-sec">
              <div className="pf-sec__row">
                <div>
                  <div>Пароль</div>
                  <div className="pf-sec__hint">обновлён недавно</div>
                </div>
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost"
                  data-testid="profile-change-password"
                  onClick={() => setSecurityView('change-password')}
                >
                  Изменить пароль
                </button>
              </div>
              <div className="pf-sec__row">
                <div>
                  <div>Двухфакторная аутентификация</div>
                  <div className="pf-sec__hint">не настроена</div>
                </div>
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost"
                  data-testid="profile-enable-2fa"
                  onClick={stub('Подключение 2FA скоро будет доступно.')}
                >
                  Подключить 2FA
                </button>
              </div>
              <div className="pf-sec__row pf-sec__row--last">
                <div>
                  <div>Активные сессии</div>
                  <div className="pf-sec__hint">управление устройствами</div>
                </div>
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost"
                  data-testid="profile-sessions"
                  onClick={stub('Просмотр активных сессий скоро будет доступен.')}
                >
                  Посмотреть сессии
                </button>
              </div>
            </div>
          </Card>

          {/* 3. Telegram */}
          <Card>
            <div className="pf-card-head">
              <h4 className="pf-card-title">Telegram</h4>
              <button
                type="button"
                className="pf-btn pf-btn--ghost"
                data-testid="profile-telegram-unlink"
                onClick={stub('Привязка Telegram скоро будет доступна в профиле.')}
              >
                Отвязать
              </button>
            </div>
            <div className="pf-tg">
              <div>
                <div className="pf-tg__handle">Не подключён</div>
                <div className="pf-sec__hint">
                  Подключите Telegram, чтобы получать уведомления
                </div>
              </div>
              <span className="pf-chip pf-chip--muted">Нет привязки</span>
            </div>
          </Card>

          {/* 4. Уведомления */}
          <Card>
            <div className="pf-card-head">
              <h4 className="pf-card-title">Уведомления</h4>
              <button
                type="button"
                className="pf-btn pf-btn--ghost"
                data-testid="profile-edit-notifications"
                onClick={stub('Настройка каналов уведомлений скоро будет доступна.')}
              >
                Изменить
              </button>
            </div>
            <div className="pf-channels">
              <ChannelRow on>
                Нарушение норматива реакции (SLA) — в <strong>Telegram</strong>
              </ChannelRow>
              <ChannelRow on>
                Сводка за день — по <strong>Email</strong> в 08:00
              </ChannelRow>
              <ChannelRow on={false}>
                Критичные инциденты — по <strong>SMS</strong>
              </ChannelRow>
              <ChannelRow on>
                Новые отзывы (≤3★) — в <strong>Telegram</strong>
              </ChannelRow>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

/**
 * Гостевой профиль (роль guest). Фамилия + номер комнаты из активного
 * гранта (/v1/me), без email/редактирования/Безопасности. Telegram —
 * «Привязать»/«Отвязать» по tg_bound. Уведомления — рабочие ползунки.
 */
function GuestProfile({ name }: { name: string }): JSX.Element {
  const me = useMe();
  const prefs = useNotificationPreferences();
  const toggle = useToggleGuestCategory();
  const bind = useTelegramBindLink();
  const [banner, setBanner] = useState<string | null>(null);

  const surname = me.data?.surname || name;
  const roomNumber = me.data?.room_number || '—';
  const tgBound = me.data?.tg_bound === true;

  const onToggle = (category: GuestNotifCategory, next: boolean): void => {
    toggle.mutate({ category, on: next });
  };

  const onBind = (): void => {
    bind.mutate(undefined, {
      onSuccess: (res) => {
        setBanner(null);
        window.open(res.deep_link, '_blank', 'noopener');
      },
      onError: () => {
        setBanner('Не удалось получить ссылку привязки. Попробуйте позже.');
      },
    });
  };

  const statusOn = isCategoryOn(prefs.data, 'request_status');
  const chatOn = isCategoryOn(prefs.data, 'request_chat');
  const togglesDisabled = prefs.isLoading || toggle.isPending;

  return (
    <section data-testid="module-profile" className="pf">
      {banner ? (
        <div className="pf-banner" role="status" data-testid="profile-hint">
          {banner}
        </div>
      ) : null}

      <div className="pf-grid">
        {/* 1. Личные данные — гость: фамилия + номер комнаты, без «Изменить» */}
        <Card>
          <div className="pf-card-head">
            <h4 className="pf-card-title">Личные данные</h4>
          </div>

          <div className="pf-identity">
            <span className="ta-avatar ta-avatar--lg" aria-hidden="true">
              {initials(surname)}
            </span>
            <div>
              <div className="pf-identity__name" data-testid="guest-surname">{surname}</div>
              <div className="pf-identity__badge">Гость</div>
            </div>
          </div>

          <div className="pf-fields">
            <Field label="Фамилия" value={surname} />
            <Field label="Номер комнаты" value={roomNumber} />
          </div>

          <p className="pf-footnote">
            Данные берутся из вашего проживания и не редактируются здесь.
          </p>
        </Card>

        <div className="pf-stack">
          {/* 3. Telegram — Привязать / Отвязать по статусу */}
          <Card>
            <div className="pf-card-head">
              <h4 className="pf-card-title">Telegram</h4>
              {tgBound ? (
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost"
                  data-testid="profile-telegram-unlink"
                  onClick={() => setBanner('Отвязка Telegram скоро будет доступна.')}
                >
                  Отвязать
                </button>
              ) : (
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost"
                  data-testid="profile-telegram-link"
                  disabled={bind.isPending}
                  onClick={onBind}
                >
                  Привязать
                </button>
              )}
            </div>
            <div className="pf-tg">
              <div>
                <div className="pf-tg__handle">{tgBound ? 'Подключён' : 'Не подключён'}</div>
                <div className="pf-sec__hint">
                  {tgBound
                    ? 'Уведомления приходят в Telegram'
                    : 'Привяжите Telegram, чтобы получать уведомления'}
                </div>
              </div>
              <span className={`pf-chip ${tgBound ? 'pf-chip--lock' : 'pf-chip--muted'}`}>
                {tgBound ? 'Привязан' : 'Нет привязки'}
              </span>
            </div>
          </Card>

          {/* 4. Уведомления — рабочие ползунки, без «Изменить», без SLA */}
          <Card>
            <div className="pf-card-head">
              <h4 className="pf-card-title">Уведомления</h4>
            </div>
            <div className="pf-channels" data-testid="guest-notif-channels">
              <GuestToggleRow
                label={
                  <>
                    Изменение статуса <strong>моей заявки</strong>
                  </>
                }
                category="request_status"
                on={statusOn}
                disabled={togglesDisabled}
                onToggle={onToggle}
                testid="toggle-request-status"
              />
              <GuestToggleRow
                label={
                  <>
                    Новое <strong>сообщение в чате</strong> по заявке
                  </>
                }
                category="request_chat"
                on={chatOn}
                disabled={togglesDisabled}
                onToggle={onToggle}
                testid="toggle-request-chat"
              />
            </div>
            <p className="pf-footnote">Переключение сохраняется сразу.</p>
          </Card>
        </div>
      </div>
    </section>
  );
}
